import { httpClient } from '../client';
import {
  User,
  UserResponse,
  SingleUserResponse,
  CreateUserDTO,
  UpdateUserDTO,
  RequestNewUserData,
  ChatUser,
  CognitoUserResponse,
} from '../../../types/user';
import { logger } from '../../../utils/logger';
import { ApiErrorHandler } from '../error.handler';
import { BaseEndpoint } from './base.endpoint';
import axios, { AxiosResponse } from 'axios';

class UserEndpoints extends BaseEndpoint {
  private static instance: UserEndpoints;

  private constructor() {
    super('client');
  }

  static getInstance(): UserEndpoints {
    if (!UserEndpoints.instance) {
      UserEndpoints.instance = new UserEndpoints();
    }
    return UserEndpoints.instance;
  }

  async listUsers(organizationId: string): Promise<User[]> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<UserResponse>(
        `${this.basePath}/${organizationId}/user`,
        { headers }
      );

      logger.info('Users fetched successfully');
      return response.data.data;
    } catch (error) {
      logger.error('Failed to fetch users:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async createUser(organizationId: string, data: CreateUserDTO): Promise<User> {
    try {
      const headers = await this.getAuthHeaders();

      // Then create user in database
      const response = await httpClient.post<SingleUserResponse>(
        `${this.basePath}/${organizationId}/user`,
        {
          ...data,
        },
        { headers }
      );

      logger.info('User created successfully');
      return response.data.data;
    } catch (error) {
      logger.error('Failed to create user:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async createRequestedUser(organizationId: string, data: RequestNewUserData) {
    try {
      const headers = await this.getAuthHeaders();

      const response = await httpClient.post(
        `${this.basePath}/${organizationId}/user/request`,
        {
          ...data,
        },
        { headers }
      );

      return response.data.data;
    } catch (error) {}
  }

  ///organisation/userrequest
  async getRequstedUser(id: string, uuid: string) {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_PUBLIC_API_BASE_URL}/organisation/userrequest`,
        {
          orgId: id,
          uuid,
        }
      );
      return response.data.items;
    } catch (error) {
      logger.error('Failed to fetch organizations:', error);
      throw error;
    }
  }

  async getChatUserByUuid(orgId: string, uuid: string): Promise<ChatUser[]> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await httpClient.get<{ message: string; data: ChatUser[] }>(
        `${this.basePath}/${orgId}/chatusers/${uuid}`,
        {
          headers,
        }
      );

      return response.data.data;
    } catch (error) {
      logger.error('Failed to fetch chat users:', error);
      throw error;
    }
  }

  async updateUser(organizationId: string, cognitoId: string, data: UpdateUserDTO): Promise<User> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.put<SingleUserResponse>(
        `${this.basePath}/${organizationId}/user/${cognitoId}`,
        data,
        { headers }
      );

      logger.info('User updated successfully');
      return response.data.data;
    } catch (error) {
      logger.error('Failed to update user:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async deleteUser(organizationId: string, cognitoId: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();

      // Then delete from database
      await httpClient.delete(`${this.basePath}/${organizationId}/user/${cognitoId}`, { headers });

      logger.info('User deleted successfully');
    } catch (error) {
      logger.error('Failed to delete user:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async getCognitoUser(mailId: string): Promise<CognitoUserResponse> {
    try {
      const response: AxiosResponse<CognitoUserResponse> = await axios.post<CognitoUserResponse>(
        `${import.meta.env.VITE_PUBLIC_API_BASE_URL}/cognitouser/request`,
        {
          mailId,
        }
      );
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch cognito user:', error);
      throw error;
    }
  }

  /**
   * Create initial admin user during activation (public endpoint, no auth required)
   * This is used for OAuth users who may not have a valid session during activation
   */
  async createInitialUser(organizationId: string, data: CreateUserDTO): Promise<User> {
    try {
      const response = await axios.post<SingleUserResponse>(
        `${import.meta.env.VITE_PUBLIC_API_BASE_URL}/organisations/${organizationId}/initial-user`,
        data
      );

      logger.info('Initial user created successfully');
      return response.data.data;
    } catch (error) {
      logger.error('Failed to create initial user:', error);
      throw error;
    }
  }
}
export const userEndpoints = UserEndpoints.getInstance();
